"use client";

import { useState, useMemo } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";
import Link from "next/link";
import ComparisonBar from "./ComparisonBar";
import ComparisonTable from "./ComparisonTable";
import { InventoryBadge, InventoryProvider } from './InventoryBadge';

type Product = {
  id: string;
  category: string;
  name: string;
  nameZh: string;
  brand: string;
  specs: string;
  image: string;
  alt: string;
  badge: string;
  badgeColor: string;
  moq: string;
  priceRange: [number, number];
  stockStatus: "in-stock" | "low-stock" | "out-of-stock";
};

const categories = [
{ id: "all", label: "全部 All", icon: "Squares2X2Icon" },
{ id: "cpu", label: "CPU 處理器", icon: "CpuChipIcon" },
{ id: "gpu", label: "GPU 顯示卡", icon: "ComputerDesktopIcon" },
{ id: "ram", label: "RAM 記憶體", icon: "ServerIcon" },
{ id: "motherboard", label: "主機板 Motherboard", icon: "CircuitBoardIcon" },
{ id: "storage", label: "Storage 儲存", icon: "ArchiveBoxIcon" },
{ id: "cooling", label: "Cooling 散熱", icon: "CloudIcon" },
{ id: "psu", label: "PSU 電源供應器", icon: "BoltIcon" },
{ id: "case", label: "Case 機殼", icon: "CubeIcon" },
{ id: "peripheral", label: "Peripherals 週邊", icon: "DeviceTabletIcon" }];

const BRANDS = [
"Intel", "AMD", "NVIDIA", "ASUS", "MSI", "Gigabyte", "ASRock",
"Corsair", "G.Skill", "Kingston", "Samsung", "WD", "Seagate",
"Noctua", "be quiet!", "Cooler Master", "NZXT", "Fractal Design",
"Lian Li", "Phanteks", "EVGA", "Seasonic", "Thermaltake",
"Logitech", "Razer", "SteelSeries", "HyperX", "Keychron"];


const STOCK_OPTIONS = [
{ id: "in-stock", label: "In Stock", labelZh: "有貨", color: "bg-green-500" },
{ id: "low-stock", label: "Low Stock", labelZh: "少量", color: "bg-yellow-500" },
{ id: "out-of-stock", label: "Out of Stock", labelZh: "缺貨", color: "bg-red-500" }];


const products: Product[] = [
// ── CPU ──────────────────────────────────────────────────────────────
{
  id: "cpu-1",
  category: "cpu",
  name: "Intel Core i9-14900K",
  nameZh: "英特爾 第14代旗艦處理器",
  brand: "Intel",
  specs: "24 Cores / 5.8GHz / LGA1700 / 125W TDP",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1026689ec-1772199577905.png",
  alt: "Intel Core i9-14900K 14th Gen processor official product image",
  badge: "Hot",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 10 units",
  priceRange: [550, 650],
  stockStatus: "in-stock"
},
{
  id: "cpu-2",
  category: "cpu",
  name: "Intel Core i9-13900KS",
  nameZh: "英特爾 第13代旗艦處理器",
  brand: "Intel",
  specs: "24 Cores / 6.0GHz Boost / LGA1700 / 150W TDP",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ff0db390-1772199576726.png",
  alt: "Intel Core i9-13900KS 13th Gen Special Edition processor official product image",
  badge: "6GHz",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 10 units",
  priceRange: [500, 600],
  stockStatus: "in-stock"
},
{
  id: "cpu-3",
  category: "cpu",
  name: "Intel Core i7-14700K",
  nameZh: "英特爾 第14代高階處理器",
  brand: "Intel",
  specs: "20 Cores / 5.6GHz / LGA1700 / 125W TDP",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bb5538ae-1772199572848.png",
  alt: "Intel Core i7-14700K 14th Gen processor official product image",
  badge: "New",
  badgeColor: "bg-yellow-brand text-navy",
  moq: "MOQ: 10 units",
  priceRange: [380, 430],
  stockStatus: "in-stock"
},
{
  id: "cpu-4",
  category: "cpu",
  name: "Intel Core i7-13700K",
  nameZh: "英特爾 第13代高階處理器",
  brand: "Intel",
  specs: "16 Cores / 5.4GHz / LGA1700 / 125W TDP",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17017cce5-1772199578979.png",
  alt: "Intel Core i7-13700K 13th Gen processor official product image",
  badge: "Popular",
  badgeColor: "bg-navy text-white",
  moq: "MOQ: 10 units",
  priceRange: [320, 380],
  stockStatus: "low-stock"
},
{
  id: "cpu-5",
  category: "cpu",
  name: "Intel Core i5-14600K",
  nameZh: "英特爾 第14代主流處理器",
  brand: "Intel",
  specs: "14 Cores / 5.3GHz / LGA1700 / 125W TDP",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17dfd1fed-1772199571755.png",
  alt: "Intel Core i5-14600K 14th Gen processor official product image",
  badge: "Best Seller",
  badgeColor: "bg-navy text-white",
  moq: "MOQ: 20 units",
  priceRange: [280, 320],
  stockStatus: "in-stock"
},
{
  id: "cpu-6",
  category: "cpu",
  name: "Intel Core i5-13600K",
  nameZh: "英特爾 第13代主流處理器",
  brand: "Intel",
  specs: "14 Cores / 5.1GHz / LGA1700 / 125W TDP",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1579e46cf-1772199582627.png",
  alt: "Intel Core i5-13600K 13th Gen processor official product image",
  badge: "Value",
  badgeColor: "bg-green-600 text-white",
  moq: "MOQ: 20 units",
  priceRange: [240, 280],
  stockStatus: "in-stock"
},
{
  id: "cpu-7",
  category: "cpu",
  name: "Intel Xeon Silver 4316",
  nameZh: "英特爾伺服器處理器",
  brand: "Intel",
  specs: "20 Cores / 3.4GHz / FCLGA4189 / Server Grade",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_131cee270-1772199575850.png",
  alt: "Intel Xeon Silver 4316 server processor official product image",
  badge: "Enterprise",
  badgeColor: "bg-lightblue text-white",
  moq: "MOQ: 5 units",
  priceRange: [800, 1000],
  stockStatus: "low-stock"
},
{
  id: "cpu-8",
  category: "cpu",
  name: "AMD Ryzen 9 9950X",
  nameZh: "AMD Zen 5 旗艦處理器",
  brand: "AMD",
  specs: "16 Cores / 5.7GHz / AM5 / Zen 5 Architecture",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_172aaf8e6-1772199577180.png",
  alt: "AMD Ryzen 9 9950X Zen 5 processor official product image",
  badge: "2025",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 10 units",
  priceRange: [600, 700],
  stockStatus: "in-stock"
},
{
  id: "cpu-9",
  category: "cpu",
  name: "AMD Ryzen 9 9900X",
  nameZh: "AMD Zen 5 高階處理器",
  brand: "AMD",
  specs: "12 Cores / 5.6GHz / AM5 / Zen 5 / 120W TDP",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_111302523-1772199578736.png",
  alt: "AMD Ryzen 9 9900X Zen 5 processor official product image",
  badge: "New",
  badgeColor: "bg-yellow-brand text-navy",
  moq: "MOQ: 10 units",
  priceRange: [450, 550],
  stockStatus: "in-stock"
},
{
  id: "cpu-10",
  category: "cpu",
  name: "AMD Ryzen 9 7950X",
  nameZh: "AMD 旗艦桌上型處理器",
  brand: "AMD",
  specs: "16 Cores / 5.7GHz / AM5 Socket / 170W TDP",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17573158e-1772199578070.png",
  alt: "AMD Ryzen 9 7950X Zen 4 processor official product image",
  badge: "Popular",
  badgeColor: "bg-navy text-white",
  moq: "MOQ: 10 units",
  priceRange: [500, 600],
  stockStatus: "low-stock"
},
{
  id: "cpu-11",
  category: "cpu",
  name: "AMD Ryzen 7 9700X",
  nameZh: "AMD Zen 5 主流處理器",
  brand: "AMD",
  specs: "8 Cores / 5.5GHz / AM5 / 65W TDP",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_183a3fd70-1772199572461.png",
  alt: "AMD Ryzen 7 9700X Zen 5 processor official product image",
  badge: "Value",
  badgeColor: "bg-green-600 text-white",
  moq: "MOQ: 15 units",
  priceRange: [320, 380],
  stockStatus: "in-stock"
},
{
  id: "cpu-12",
  category: "cpu",
  name: "AMD Ryzen 7 7700X",
  nameZh: "AMD Zen 4 高效處理器",
  brand: "AMD",
  specs: "8 Cores / 5.4GHz / AM5 / 105W TDP",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ef96da7c-1772174254938.png",
  alt: "AMD Ryzen 7 7700X Zen 4 processor official product image",
  badge: "Efficient",
  badgeColor: "bg-green-600 text-white",
  moq: "MOQ: 15 units",
  priceRange: [280, 340],
  stockStatus: "in-stock"
},
{
  id: "cpu-13",
  category: "cpu",
  name: "AMD Ryzen 5 9600X",
  nameZh: "AMD Zen 5 入門處理器",
  brand: "AMD",
  specs: "6 Cores / 5.4GHz / AM5 / 65W TDP",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fd9874a8-1772199576646.png",
  alt: "AMD Ryzen 5 9600X Zen 5 processor official product image",
  badge: "Budget",
  badgeColor: "bg-green-600 text-white",
  moq: "MOQ: 25 units",
  priceRange: [200, 250],
  stockStatus: "in-stock"
},
{
  id: "cpu-14",
  category: "cpu",
  name: "AMD Ryzen 5 7600X",
  nameZh: "AMD Zen 4 主流處理器",
  brand: "AMD",
  specs: "6 Cores / 5.3GHz / AM5 / 105W TDP",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_15b4c89a0-1772174256385.png",
  alt: "AMD Ryzen 5 7600X Zen 4 processor official product image",
  badge: "Best Value",
  badgeColor: "bg-navy text-white",
  moq: "MOQ: 25 units",
  priceRange: [180, 230],
  stockStatus: "in-stock"
},
// ── GPU ──────────────────────────────────────────────────────────────
{
  id: "gpu-1",
  category: "gpu",
  name: "ASUS ROG Strix RTX 4090 OC",
  nameZh: "華碩 ROG Strix 旗艦顯示卡",
  brand: "ASUS",
  specs: "24GB GDDR6X / PCIe 4.0 / 450W TDP / Triple Fan",
  image: "https://dlcdnwebimgs.asus.com/gain/ROG-STRIX-RTX4090-O24G-GAMING/w1000/h732",
  alt: "ASUS ROG Strix GeForce RTX 4090 OC Edition 24GB graphics card with triple fan cooling",
  badge: "Premium",
  badgeColor: "bg-green-600 text-white",
  moq: "MOQ: 5 units",
  priceRange: [1600, 2100],
  stockStatus: "low-stock"
},
{
  id: "gpu-2",
  category: "gpu",
  name: "ASUS TUF Gaming RTX 4080 SUPER OC",
  nameZh: "華碩 TUF Gaming 高階顯示卡",
  brand: "ASUS",
  specs: "16GB GDDR6X / PCIe 4.0 / 320W TDP / Triple Fan",
  image: "https://dlcdnwebimgs.asus.com/gain/TUF-RTX4080S-O16G-GAMING/w1000/h732",
  alt: "ASUS TUF Gaming GeForce RTX 4080 SUPER OC Edition 16GB graphics card",
  badge: "Hot",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 8 units",
  priceRange: [980, 1150],
  stockStatus: "in-stock"
},
{
  id: "gpu-3",
  category: "gpu",
  name: "ASUS ROG Strix RTX 4070 Ti SUPER OC",
  nameZh: "華碩 ROG Strix 高效能顯示卡",
  brand: "ASUS",
  specs: "16GB GDDR6X / PCIe 4.0 / 285W TDP / Triple Fan",
  image: "https://dlcdnwebimgs.asus.com/gain/ROG-STRIX-RTX4070TIS-O16G-GAMING/w1000/h732",
  alt: "ASUS ROG Strix GeForce RTX 4070 Ti SUPER OC Edition 16GB graphics card",
  badge: "Popular",
  badgeColor: "bg-navy text-white",
  moq: "MOQ: 10 units",
  priceRange: [720, 850],
  stockStatus: "in-stock"
},
{
  id: "gpu-4",
  category: "gpu",
  name: "MSI GeForce RTX 4090 Gaming X Trio",
  nameZh: "微星 Gaming X Trio 旗艦顯示卡",
  brand: "MSI",
  specs: "24GB GDDR6X / PCIe 4.0 / 450W TDP / TORX Fan 5.0",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d1b36271-1768384675633.png",
  alt: "MSI GeForce RTX 4090 Gaming X Trio 24GB graphics card with TORX Fan 5.0 triple cooling",
  badge: "Flagship",
  badgeColor: "bg-red-600 text-white",
  moq: "MOQ: 5 units",
  priceRange: [1550, 2050],
  stockStatus: "low-stock"
},
{
  id: "gpu-5",
  category: "gpu",
  name: "MSI RTX 4080 SUPER Suprim X",
  nameZh: "微星 Suprim X 高階顯示卡",
  brand: "MSI",
  specs: "16GB GDDR6X / PCIe 4.0 / 320W TDP / Tri Frozr 3S",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d1b36271-1768384675633.png",
  alt: "MSI GeForce RTX 4080 SUPER Suprim X 16GB graphics card with Tri Frozr 3S cooling",
  badge: "Best Seller",
  badgeColor: "bg-navy text-white",
  moq: "MOQ: 8 units",
  priceRange: [960, 1120],
  stockStatus: "in-stock"
},
{
  id: "gpu-6",
  category: "gpu",
  name: "MSI RTX 4070 SUPER Gaming X Trio",
  nameZh: "微星 Gaming X Trio 主流顯示卡",
  brand: "MSI",
  specs: "12GB GDDR6X / PCIe 4.0 / 220W TDP / TORX Fan 5.0",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d1b36271-1768384675633.png",
  alt: "MSI GeForce RTX 4070 SUPER Gaming X Trio 12GB graphics card",
  badge: "Value",
  badgeColor: "bg-green-600 text-white",
  moq: "MOQ: 10 units",
  priceRange: [560, 660],
  stockStatus: "in-stock"
},
{
  id: "gpu-7",
  category: "gpu",
  name: "Gigabyte AORUS RTX 4090 Master",
  nameZh: "技嘉 AORUS 旗艦顯示卡",
  brand: "Gigabyte",
  specs: "24GB GDDR6X / PCIe 4.0 / 450W TDP / WINDFORCE Stack",
  image: "https://static.gigabyte.com/StaticFile/Image/Global/GV-N4090AORUS-M-24GD/Product/28000/png/1000",
  alt: "Gigabyte AORUS GeForce RTX 4090 Master 24GB graphics card with WINDFORCE Stack cooling",
  badge: "Premium",
  badgeColor: "bg-green-600 text-white",
  moq: "MOQ: 5 units",
  priceRange: [1580, 2080],
  stockStatus: "low-stock"
},
{
  id: "gpu-8",
  category: "gpu",
  name: "Gigabyte RTX 4080 SUPER Gaming OC",
  nameZh: "技嘉 Gaming OC 高階顯示卡",
  brand: "Gigabyte",
  specs: "16GB GDDR6X / PCIe 4.0 / 320W TDP / WINDFORCE 3X",
  image: "https://static.gigabyte.com/StaticFile/Image/Global/GV-N408SGAMING-OC-16GD/Product/28001/png/1000",
  alt: "Gigabyte GeForce RTX 4080 SUPER Gaming OC 16GB graphics card with WINDFORCE 3X cooling",
  badge: "New",
  badgeColor: "bg-yellow-brand text-navy",
  moq: "MOQ: 8 units",
  priceRange: [940, 1100],
  stockStatus: "in-stock"
},
{
  id: "gpu-9",
  category: "gpu",
  name: "ASUS TUF Gaming RX 7900 XTX OC",
  nameZh: "華碩 TUF Gaming AMD 旗艦顯示卡",
  brand: "ASUS",
  specs: "24GB GDDR6 / PCIe 4.0 / 355W TDP / Triple Fan",
  image: "https://dlcdnwebimgs.asus.com/gain/TUF-RX7900XTX-O24G-GAMING/w1000/h732",
  alt: "ASUS TUF Gaming Radeon RX 7900 XTX OC Edition 24GB graphics card",
  badge: "Flagship",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 8 units",
  priceRange: [920, 1080],
  stockStatus: "low-stock"
},
{
  id: "gpu-10",
  category: "gpu",
  name: "MSI RX 7800 XT Gaming X Trio",
  nameZh: "微星 Gaming X Trio AMD 高階顯示卡",
  brand: "MSI",
  specs: "16GB GDDR6 / PCIe 4.0 / 263W TDP / TORX Fan 5.0",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_14a090c85-1772200746588.png",
  alt: "MSI Radeon RX 7800 XT Gaming X Trio 16GB graphics card with TORX Fan 5.0",
  badge: "Hot",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 10 units",
  priceRange: [460, 560],
  stockStatus: "in-stock"
},
{
  id: "gpu-11",
  category: "gpu",
  name: "Gigabyte AORUS RX 7700 XT Elite",
  nameZh: "技嘉 AORUS AMD 主流顯示卡",
  brand: "Gigabyte",
  specs: "12GB GDDR6 / PCIe 4.0 / 245W TDP / WINDFORCE 3X",
  image: "https://static.gigabyte.com/StaticFile/Image/Global/GV-R77XTAORUS-E-12GD/Product/28002/png/1000",
  alt: "Gigabyte AORUS Radeon RX 7700 XT Elite 12GB graphics card with WINDFORCE 3X cooling",
  badge: "Value",
  badgeColor: "bg-green-600 text-white",
  moq: "MOQ: 12 units",
  priceRange: [360, 440],
  stockStatus: "in-stock"
},
{
  id: "gpu-12",
  category: "gpu",
  name: "MSI RX 7600 Gaming X",
  nameZh: "微星 Gaming X AMD 入門顯示卡",
  brand: "MSI",
  specs: "8GB GDDR6 / PCIe 4.0 / 165W TDP / TORX Fan 4.0",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_141eeef88-1772200751881.png",
  alt: "MSI Radeon RX 7600 Gaming X 8GB graphics card with TORX Fan 4.0 dual cooling",
  badge: "Budget",
  badgeColor: "bg-green-600 text-white",
  moq: "MOQ: 20 units",
  priceRange: [230, 290],
  stockStatus: "in-stock"
},
// ── RAM ──────────────────────────────────────────────────────────────
{
  id: "ram-1",
  category: "ram",
  name: "Corsair Dominator Platinum DDR5-6000 96GB",
  nameZh: "海盜船 DDR5 旗艦記憶體",
  brand: "Corsair",
  specs: "96GB (2×48GB) / DDR5-6000 / CL30 / RGB",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1967f87ab-1765244545316.png",
  alt: "Corsair Dominator Platinum RGB DDR5-6000 96GB memory kit official product image",
  badge: "96GB",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 10 units",
  priceRange: [350, 450],
  stockStatus: "in-stock"
},
{
  id: "ram-2",
  category: "ram",
  name: "G.Skill Trident Z5 DDR5-6400 64GB",
  nameZh: "芝奇 DDR5 高頻記憶體",
  brand: "G.Skill",
  specs: "64GB (2×32GB) / DDR5-6400 / CL32 / RGB",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f7cf6c32-1771516325185.png",
  alt: "G.Skill Trident Z5 DDR5-6400 64GB RGB high-frequency RAM kit",
  badge: "6400MHz",
  badgeColor: "bg-yellow-brand text-navy",
  moq: "MOQ: 10 units",
  priceRange: [280, 350],
  stockStatus: "in-stock"
},
{
  id: "ram-3",
  category: "ram",
  name: "Kingston Fury Beast DDR5-5600 32GB",
  nameZh: "金士頓 DDR5 主流記憶體",
  brand: "Kingston",
  specs: "32GB (2×16GB) / DDR5-5600 / CL36",
  image: "https://www.kingston.com/dataSheets/KF556C40BBK2-32_en.pdf",
  alt: "Kingston Fury Beast DDR5-5600 32GB memory kit official product image",
  badge: "Popular",
  badgeColor: "bg-navy text-white",
  moq: "MOQ: 15 units",
  priceRange: [120, 160],
  stockStatus: "in-stock"
},
{
  id: "ram-4",
  category: "ram",
  name: "Corsair Vengeance DDR5-5200 48GB",
  nameZh: "海盜船 DDR5 高容量記憶體",
  brand: "Corsair",
  specs: "48GB (2×24GB) / DDR5-5200 / CL38",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1818a9052-1772199573182.png",
  alt: "Corsair Vengeance DDR5-5200 48GB memory kit official product image",
  badge: "48GB",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 12 units",
  priceRange: [160, 210],
  stockStatus: "in-stock"
},
{
  id: "ram-5",
  category: "ram",
  name: "G.Skill Ripjaws S5 DDR5-6000 32GB",
  nameZh: "芝奇 DDR5 電競記憶體",
  brand: "G.Skill",
  specs: "32GB (2×16GB) / DDR5-6000 / CL30",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fd3a00e8-1772174254614.png",
  alt: "G.Skill Ripjaws S5 DDR5-6000 32GB gaming RAM kit",
  badge: "Gaming",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 15 units",
  priceRange: [130, 170],
  stockStatus: "in-stock"
},
{
  id: "ram-6",
  category: "ram",
  name: "Samsung DDR5-4800 ECC 64GB",
  nameZh: "三星 DDR5 伺服器記憶體",
  brand: "Samsung",
  specs: "64GB / DDR5-4800 / ECC / Server Grade",
  image: "https://semiconductor.samsung.com/us/consumer-storage/internal-ssd/990-pro/",
  alt: "Samsung DDR5-4800 ECC 64GB server-grade RAM module official product image",
  badge: "Server",
  badgeColor: "bg-lightblue text-white",
  moq: "MOQ: 8 units",
  priceRange: [300, 400],
  stockStatus: "low-stock"
},
// ── MOTHERBOARD ───────────────────────────────────────────────────────
{
  id: "mb-1",
  category: "motherboard",
  name: "ASUS ROG Maximus Z790 Hero",
  nameZh: "華碩 ROG Z790 旗艦主機板",
  brand: "ASUS",
  specs: "Z790 / LGA1700 / DDR5 / PCIe 5.0 / WiFi 6E",
  image: "https://dlcdnwebimgs.asus.com/gain/B5B2E6B3-0E1A-4B0A-8E9A-4B0A8E9A4B0A/w1000/h732",
  alt: "ASUS ROG Maximus Z790 Hero flagship motherboard official product image",
  badge: "Flagship",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 5 units",
  priceRange: [600, 750],
  stockStatus: "in-stock"
},
{
  id: "mb-2",
  category: "motherboard",
  name: "MSI MEG Z790 ACE",
  nameZh: "微星 MEG Z790 旗艦主機板",
  brand: "MSI",
  specs: "Z790 / LGA1700 / DDR5 / PCIe 5.0 / 2.5G LAN",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a9a43949-1772172700634.png",
  alt: "MSI MEG Z790 ACE flagship motherboard official product image",
  badge: "Premium",
  badgeColor: "bg-navy text-white",
  moq: "MOQ: 5 units",
  priceRange: [500, 650],
  stockStatus: "in-stock"
},
{
  id: "mb-3",
  category: "motherboard",
  name: "Gigabyte Z790 AORUS Master",
  nameZh: "技嘉 Z790 AORUS 旗艦主機板",
  brand: "Gigabyte",
  specs: "Z790 / LGA1700 / DDR5 / PCIe 5.0 / Thunderbolt 4",
  image: "https://static.gigabyte.com/StaticFile/Image/Global/e5e7e9e9e9e9e9e9e9e9e9e9e9e9e9e9/Product/30000/png/1000",
  alt: "Gigabyte Z790 AORUS Master motherboard official product image",
  badge: "Hot",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 5 units",
  priceRange: [450, 580],
  stockStatus: "in-stock"
},
{
  id: "mb-4",
  category: "motherboard",
  name: "ASUS TUF Gaming X670E-Plus",
  nameZh: "華碩 TUF X670E 主機板",
  brand: "ASUS",
  specs: "X670E / AM5 / DDR5 / PCIe 5.0 / WiFi 6",
  image: "https://dlcdnwebimgs.asus.com/gain/F2A3B4C5-1234-5678-ABCD-EF0123456789/w1000/h732",
  alt: "ASUS TUF Gaming X670E-Plus AMD AM5 motherboard official product image",
  badge: "Popular",
  badgeColor: "bg-navy text-white",
  moq: "MOQ: 8 units",
  priceRange: [280, 350],
  stockStatus: "in-stock"
},
{
  id: "mb-5",
  category: "motherboard",
  name: "MSI MAG B650 Tomahawk WiFi",
  nameZh: "微星 B650 主流主機板",
  brand: "MSI",
  specs: "B650 / AM5 / DDR5 / PCIe 4.0 / WiFi 6E",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19a2d2507-1772199572926.png",
  alt: "MSI MAG B650 Tomahawk WiFi AMD AM5 mid-range motherboard official product image",
  badge: "Value",
  badgeColor: "bg-green-600 text-white",
  moq: "MOQ: 10 units",
  priceRange: [180, 240],
  stockStatus: "low-stock"
},
{
  id: "mb-6",
  category: "motherboard",
  name: "Gigabyte B760M DS3H",
  nameZh: "技嘉 B760M 入門主機板",
  brand: "Gigabyte",
  specs: "B760M / LGA1700 / DDR5 / Micro-ATX",
  image: "https://static.gigabyte.com/StaticFile/Image/Global/d4d4d4d4d4d4d4d4d4d4d4d4d4d4d4d4/Product/30001/png/1000",
  alt: "Gigabyte B760M DS3H budget Intel LGA1700 motherboard official product image",
  badge: "Budget",
  badgeColor: "bg-green-600 text-white",
  moq: "MOQ: 15 units",
  priceRange: [100, 140],
  stockStatus: "in-stock"
},
{
  id: "mb-7",
  category: "motherboard",
  name: "ASRock X670E Taichi",
  nameZh: "華擎 X670E 旗艦主機板",
  brand: "ASRock",
  specs: "X670E / AM5 / DDR5 / PCIe 5.0 / Thunderbolt 4",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1668ef59e-1771516321403.png",
  alt: "ASRock X670E Taichi flagship AMD AM5 motherboard",
  badge: "Flagship",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 5 units",
  priceRange: [400, 520],
  stockStatus: "low-stock"
},
// ── STORAGE ───────────────────────────────────────────────────────────
{
  id: "storage-1",
  category: "storage",
  name: "Samsung 990 Pro 2TB NVMe",
  nameZh: "三星 Gen4 旗艦固態硬碟",
  brand: "Samsung",
  specs: "2TB / PCIe 4.0 NVMe / 7450MB/s Read / M.2",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18ed0f314-1772199577681.png",
  alt: "Samsung 990 Pro 2TB NVMe SSD M.2 official product image",
  badge: "7450MB/s",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 20 units",
  priceRange: [150, 200],
  stockStatus: "in-stock"
},
{
  id: "storage-2",
  category: "storage",
  name: "WD Black SN850X 4TB",
  nameZh: "WD 旗艦 Gen4 固態硬碟",
  brand: "WD",
  specs: "4TB / PCIe 4.0 NVMe / 7300MB/s Read / M.2",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_114af22dc-1772199576663.png",
  alt: "WD Black SN850X 4TB NVMe SSD official product image",
  badge: "4TB",
  badgeColor: "bg-navy text-white",
  moq: "MOQ: 15 units",
  priceRange: [280, 360],
  stockStatus: "in-stock"
},
{
  id: "storage-3",
  category: "storage",
  name: "Seagate FireCuda 530 2TB",
  nameZh: "希捷 Gen4 電競固態硬碟",
  brand: "Seagate",
  specs: "2TB / PCIe 4.0 NVMe / 7300MB/s Read / Heatsink",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1170704cc-1771553641273.png",
  alt: "Seagate FireCuda 530 2TB NVMe SSD with heatsink",
  badge: "Gaming",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 20 units",
  priceRange: [160, 210],
  stockStatus: "in-stock"
},
{
  id: "storage-4",
  category: "storage",
  name: "Samsung 980 Pro 1TB NVMe",
  nameZh: "三星 Gen4 固態硬碟",
  brand: "Samsung",
  specs: "1TB / PCIe 4.0 NVMe / 7000MB/s Read / M.2",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19cb97295-1772199576744.png",
  alt: "Samsung 980 Pro 1TB NVMe SSD M.2 official product image",
  badge: "Popular",
  badgeColor: "bg-navy text-white",
  moq: "MOQ: 25 units",
  priceRange: [80, 120],
  stockStatus: "in-stock"
},
{
  id: "storage-5",
  category: "storage",
  name: "WD Red Pro 8TB HDD",
  nameZh: "WD 企業級硬碟",
  brand: "WD",
  specs: "8TB / 7200RPM / SATA 6Gb/s / NAS Grade",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_113e8442a-1772173165306.png",
  alt: "WD Red Pro 8TB NAS hard drive official product image",
  badge: "NAS",
  badgeColor: "bg-lightblue text-white",
  moq: "MOQ: 10 units",
  priceRange: [200, 260],
  stockStatus: "in-stock"
},
{
  id: "storage-6",
  category: "storage",
  name: "Seagate Exos X20 20TB",
  nameZh: "希捷 企業級大容量硬碟",
  brand: "Seagate",
  specs: "20TB / 7200RPM / SATA 6Gb/s / Enterprise",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19801df17-1767636468840.png",
  alt: "Seagate Exos X20 20TB enterprise hard drive",
  badge: "Enterprise",
  badgeColor: "bg-lightblue text-white",
  moq: "MOQ: 5 units",
  priceRange: [400, 500],
  stockStatus: "low-stock"
},
// ── COOLING ───────────────────────────────────────────────────────────
{
  id: "cooling-1",
  category: "cooling",
  name: "Noctua NH-D15 chromax.black",
  nameZh: "貓頭鷹 旗艦塔式散熱器",
  brand: "Noctua",
  specs: "Dual Tower / 2×140mm / LGA1700 / AM5 / 250W TDP",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fb2f8610-1772079188472.png",
  alt: "Noctua NH-D15 chromax black dual tower CPU cooler",
  badge: "Best Air",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 15 units",
  priceRange: [90, 120],
  stockStatus: "in-stock"
},
{
  id: "cooling-2",
  category: "cooling",
  name: "Corsair iCUE H150i Elite LCD",
  nameZh: "海盜船 360mm AIO 水冷",
  brand: "Corsair",
  specs: "360mm AIO / LCD Display / 3×120mm / RGB",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19052f7c1-1766540784019.png",
  alt: "Corsair iCUE H150i Elite 360mm AIO liquid cooler with LCD",
  badge: "360mm",
  badgeColor: "bg-navy text-white",
  moq: "MOQ: 10 units",
  priceRange: [180, 240],
  stockStatus: "in-stock"
},
{
  id: "cooling-3",
  category: "cooling",
  name: "be quiet! Dark Rock Pro 5",
  nameZh: "be quiet! 旗艦靜音散熱器",
  brand: "be quiet!",
  specs: "Dual Tower / 2×135mm / Silent Wings / 250W TDP",
  image: "https://images.unsplash.com/photo-1664535105653-6e783e1948f7",
  alt: "be quiet Dark Rock Pro 5 silent dual tower CPU cooler",
  badge: "Silent",
  badgeColor: "bg-lightblue text-white",
  moq: "MOQ: 15 units",
  priceRange: [80, 110],
  stockStatus: "in-stock"
},
{
  id: "cooling-4",
  category: "cooling",
  name: "NZXT Kraken Elite 360",
  nameZh: "NZXT 旗艦 AIO 水冷",
  brand: "NZXT",
  specs: "360mm AIO / LCD Head / 3×120mm / RGB",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1849a1449-1766044402940.png",
  alt: "NZXT Kraken Elite 360mm AIO cooler with LCD display head",
  badge: "Value",
  badgeColor: "bg-green-600 text-white",
  moq: "MOQ: 10 units",
  priceRange: [200, 270],
  stockStatus: "low-stock"
},
{
  id: "cooling-5",
  category: "cooling",
  name: "Cooler Master MasterLiquid 360L Core",
  nameZh: "酷冷至尊 360mm AIO 水冷",
  brand: "Cooler Master",
  specs: "360mm AIO / Dual Chamber Pump / ARGB",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_142cca901-1772173165169.png",
  alt: "Cooler Master MasterLiquid 360L Core AIO liquid cooler",
  badge: "Budget",
  badgeColor: "bg-green-600 text-white",
  moq: "MOQ: 12 units",
  priceRange: [100, 140],
  stockStatus: "in-stock"
},
{
  id: "cooling-6",
  category: "cooling",
  name: "Thermaltake TOUGHLIQUID Ultra 360",
  nameZh: "曜越 旗艦 AIO 水冷",
  brand: "Thermaltake",
  specs: "360mm AIO / 2.1\" LCD / 3×120mm ARGB",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a7d16f4c-1772173166878.png",
  alt: "Thermaltake TOUGHLIQUID Ultra 360mm AIO with 2.1 inch LCD",
  badge: "Premium",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 10 units",
  priceRange: [220, 290],
  stockStatus: "low-stock"
},
// ── PSU ───────────────────────────────────────────────────────────────
{
  id: "psu-1",
  category: "psu",
  name: "Seasonic PRIME TX-1000",
  nameZh: "海韻 1000W 旗艦電源",
  brand: "Seasonic",
  specs: "1000W / 80+ Titanium / Fully Modular / 12VHPWR",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_14f4c3faa-1772173165497.png",
  alt: "Seasonic PRIME TX-1000 1000W Titanium fully modular PSU",
  badge: "Titanium",
  badgeColor: "bg-yellow-brand text-navy",
  moq: "MOQ: 10 units",
  priceRange: [220, 280],
  stockStatus: "in-stock"
},
{
  id: "psu-2",
  category: "psu",
  name: "Corsair RM1000x SHIFT",
  nameZh: "海盜船 1000W 金牌電源",
  brand: "Corsair",
  specs: "1000W / 80+ Gold / Fully Modular / Side Connectors",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cfd6a848-1772173165957.png",
  alt: "Corsair RM1000x SHIFT 1000W Gold PSU with side connectors",
  badge: "1000W",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 10 units",
  priceRange: [180, 230],
  stockStatus: "in-stock"
},
{
  id: "psu-3",
  category: "psu",
  name: "be quiet! Dark Power 13 850W",
  nameZh: "be quiet! 850W 旗艦電源",
  brand: "be quiet!",
  specs: "850W / 80+ Titanium / Fully Modular / Silent",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b5905826-1772173166329.png",
  alt: "be quiet Dark Power 13 850W Titanium silent modular PSU",
  badge: "Silent",
  badgeColor: "bg-lightblue text-white",
  moq: "MOQ: 10 units",
  priceRange: [190, 250],
  stockStatus: "in-stock"
},
{
  id: "psu-4",
  category: "psu",
  name: "MSI MEG Ai1300P PCIE5",
  nameZh: "微星 1300W 旗艦電源",
  brand: "MSI",
  specs: "1300W / 80+ Platinum / PCIe 5.0 / ATX 3.0",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10ac9c457-1772173165131.png",
  alt: "MSI MEG Ai1300P 1300W Platinum PCIe 5.0 ATX 3.0 PSU",
  badge: "ATX 3.0",
  badgeColor: "bg-navy text-white",
  moq: "MOQ: 8 units",
  priceRange: [280, 360],
  stockStatus: "low-stock"
},
{
  id: "psu-5",
  category: "psu",
  name: "EVGA SuperNOVA 850 G7",
  nameZh: "EVGA 850W 金牌電源",
  brand: "EVGA",
  specs: "850W / 80+ Gold / Fully Modular / ECO Mode",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1affcbb80-1772173165951.png",
  alt: "EVGA SuperNOVA 850 G7 850W Gold fully modular PSU",
  badge: "Gold",
  badgeColor: "bg-yellow-brand text-navy",
  moq: "MOQ: 12 units",
  priceRange: [130, 170],
  stockStatus: "in-stock"
},
{
  id: "psu-6",
  category: "psu",
  name: "Thermaltake Toughpower GF3 750W",
  nameZh: "曜越 750W 金牌電源",
  brand: "Thermaltake",
  specs: "750W / 80+ Gold / Fully Modular / PCIe 5.0",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1111fcaeb-1765244539295.png",
  alt: "Thermaltake Toughpower GF3 750W Gold modular PSU",
  badge: "Value",
  badgeColor: "bg-green-600 text-white",
  moq: "MOQ: 15 units",
  priceRange: [100, 140],
  stockStatus: "in-stock"
},
// ── CASE ──────────────────────────────────────────────────────────────
{
  id: "case-1",
  category: "case",
  name: "Fractal Design Torrent",
  nameZh: "Fractal 高氣流機殼",
  brand: "Fractal Design",
  specs: "ATX Mid Tower / 2×180mm Front Fans / High Airflow",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1de4d842f-1765445311025.png",
  alt: "Fractal Design Torrent ATX mid tower case with 180mm front fans",
  badge: "Airflow",
  badgeColor: "bg-lightblue text-white",
  moq: "MOQ: 10 units",
  priceRange: [150, 200],
  stockStatus: "in-stock"
},
{
  id: "case-2",
  category: "case",
  name: "Lian Li O11 Dynamic EVO",
  nameZh: "聯力 O11 雙腔機殼",
  brand: "Lian Li",
  specs: "ATX Mid Tower / Dual Chamber / Tempered Glass",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c2bbdcae-1765445309346.png",
  alt: "Lian Li O11 Dynamic EVO dual chamber ATX case with tempered glass",
  badge: "Popular",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 10 units",
  priceRange: [140, 180],
  stockStatus: "in-stock"
},
{
  id: "case-3",
  category: "case",
  name: "NZXT H9 Flow",
  nameZh: "NZXT H9 雙腔機殼",
  brand: "NZXT",
  specs: "ATX Mid Tower / Dual Chamber / 4-Side Glass",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1581d7279-1772173167463.png",
  alt: "NZXT H9 Flow dual chamber ATX case with four-sided glass panels",
  badge: "Glass",
  badgeColor: "bg-lightblue text-white",
  moq: "MOQ: 10 units",
  priceRange: [160, 210],
  stockStatus: "in-stock"
},
{
  id: "case-4",
  category: "case",
  name: "Corsair 4000D Airflow",
  nameZh: "海盜船 4000D 高氣流機殼",
  brand: "Corsair",
  specs: "ATX Mid Tower / High Airflow / Tempered Glass",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_11e863ee3-1767714379845.png",
  alt: "Corsair 4000D Airflow ATX mid tower case with mesh front panel",
  badge: "Airflow",
  badgeColor: "bg-lightblue text-white",
  moq: "MOQ: 12 units",
  priceRange: [90, 120],
  stockStatus: "in-stock"
},
{
  id: "case-5",
  category: "case",
  name: "Fractal Design North",
  nameZh: "Fractal 木紋機殼",
  brand: "Fractal Design",
  specs: "ATX Mid Tower / Wood Front Panel / Mesh Side / Minimalist",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18f9d5846-1772172702608.png",
  alt: "Fractal Design North ATX case with wood front panel minimalist design",
  badge: "Design",
  badgeColor: "bg-yellow-brand text-navy",
  moq: "MOQ: 10 units",
  priceRange: [120, 160],
  stockStatus: "in-stock"
},
{
  id: "case-6",
  category: "case",
  name: "be quiet! Silent Base 802",
  nameZh: "be quiet! 靜音機殼",
  brand: "be quiet!",
  specs: "ATX Mid Tower / Sound Dampening / 3×140mm Fans / Modular",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d6703c73-1765962920401.png",
  alt: "be quiet Silent Base 802 sound dampened ATX mid tower case",
  badge: "Silent",
  badgeColor: "bg-lightblue text-white",
  moq: "MOQ: 10 units",
  priceRange: [130, 170],
  stockStatus: "in-stock"
},
{
  id: "case-7",
  category: "case",
  name: "Cooler Master HAF 700 EVO",
  nameZh: "酷冷至尊 旗艦機殼",
  brand: "Cooler Master",
  specs: "Full Tower / 200mm ARGB Fan / Tempered Glass / E-ATX",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_133f2ff4d-1765962922005.png",
  alt: "Cooler Master HAF 700 EVO full tower case with large ARGB fan",
  badge: "Full Tower",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 8 units",
  priceRange: [200, 270],
  stockStatus: "low-stock"
},
{
  id: "case-8",
  category: "case",
  name: "Phanteks Enthoo Pro II",
  nameZh: "Phanteks 旗艦全塔機殼",
  brand: "Phanteks",
  specs: "Full Tower / E-ATX / Dual System / Tempered Glass",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e71a97e7-1772172700592.png",
  alt: "Phanteks Enthoo Pro II full tower E-ATX dual system case",
  badge: "Dual Sys",
  badgeColor: "bg-navy text-white",
  moq: "MOQ: 8 units",
  priceRange: [180, 240],
  stockStatus: "in-stock"
},
// ── PERIPHERAL ────────────────────────────────────────────────────────
{
  id: "peripheral-1",
  category: "peripheral",
  name: "Logitech MX Master 3S",
  nameZh: "羅技 商務無線滑鼠",
  brand: "Logitech",
  specs: "8000 DPI / Bluetooth + USB / Ergonomic",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17057af6c-1772194910744.png",
  alt: "Logitech MX Master 3S wireless ergonomic mouse for productivity",
  badge: "Popular",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 50 units",
  priceRange: [80, 110],
  stockStatus: "in-stock"
},
{
  id: "peripheral-2",
  category: "peripheral",
  name: "Keychron K8 Pro Keyboard",
  nameZh: "Keychron 機械式鍵盤",
  brand: "Keychron",
  specs: "TKL / Hot-swap / RGB / Wireless",
  image: "https://images.unsplash.com/photo-1663518695071-180667d630a7",
  alt: "Keychron K8 Pro TKL wireless mechanical keyboard with RGB",
  badge: "Trending",
  badgeColor: "bg-yellow-brand text-navy",
  moq: "MOQ: 30 units",
  priceRange: [90, 130],
  stockStatus: "in-stock"
},
{
  id: "peripheral-3",
  category: "peripheral",
  name: "Razer DeathAdder V3 Pro",
  nameZh: "雷蛇 電競無線滑鼠",
  brand: "Razer",
  specs: "30000 DPI / Wireless / 90hr Battery / Gaming",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10080fd1d-1764792423309.png",
  alt: "Razer DeathAdder V3 Pro wireless gaming mouse ergonomic shape",
  badge: "Gaming",
  badgeColor: "bg-orange text-white",
  moq: "MOQ: 40 units",
  priceRange: [120, 160],
  stockStatus: "in-stock"
},
{
  id: "peripheral-4",
  category: "peripheral",
  name: "SteelSeries Apex Pro TKL",
  nameZh: "賽睿 電競機械鍵盤",
  brand: "SteelSeries",
  specs: "TKL / OmniPoint 2.0 / RGB / Adjustable Actuation",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_147791310-1765149942826.png",
  alt: "SteelSeries Apex Pro TKL gaming keyboard with adjustable actuation",
  badge: "Pro",
  badgeColor: "bg-navy text-white",
  moq: "MOQ: 25 units",
  priceRange: [160, 210],
  stockStatus: "in-stock"
},
{
  id: "peripheral-5",
  category: "peripheral",
  name: "Logitech G Pro X Superlight 2",
  nameZh: "羅技 專業電競滑鼠",
  brand: "Logitech",
  specs: "32000 DPI / 60g / Wireless / Esports Grade",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_104d5d70c-1765064258284.png",
  alt: "Logitech G Pro X Superlight 2 ultra-light wireless esports mouse",
  badge: "Esports",
  badgeColor: "bg-lightblue text-white",
  moq: "MOQ: 35 units",
  priceRange: [140, 180],
  stockStatus: "in-stock"
},
{
  id: "peripheral-6",
  category: "peripheral",
  name: "HyperX Cloud Alpha Wireless",
  nameZh: "HyperX 無線電競耳機",
  brand: "HyperX",
  specs: "Wireless / 300hr Battery / DTS:X / Gaming Headset",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_12eac9c82-1772172699860.png",
  alt: "HyperX Cloud Alpha Wireless gaming headset with 300 hour battery",
  badge: "Long Battery",
  badgeColor: "bg-green-600 text-white",
  moq: "MOQ: 30 units",
  priceRange: [150, 200],
  stockStatus: "low-stock"
}];

const PRICE_MIN = 0;
const PRICE_MAX = 2000;

const stockStatusConfig: Record<string, {label: string;labelZh: string;color: string;dot: string;}> = {
  "in-stock": { label: "In Stock", labelZh: "有貨", color: "text-green-600", dot: "bg-green-500" },
  "low-stock": { label: "Low Stock", labelZh: "少量", color: "text-yellow-600", dot: "bg-yellow-500" },
  "out-of-stock": { label: "Out of Stock", labelZh: "缺貨", color: "text-red-500", dot: "bg-red-500" }
};

export default function ProductCatalog() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [showComparison, setShowComparison] = useState(false);

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [priceRange, setPriceRange] = useState<[number, number]>([PRICE_MIN, PRICE_MAX]);
  const [selectedStockStatuses, setSelectedStockStatuses] = useState<string[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  // Get brands present in current category
  const availableBrands = useMemo(() => {
    const categoryProducts = activeCategory === "all" ? products : products.filter((p) => p.category === activeCategory);
    const brandSet = new Set(categoryProducts.map((p) => p.brand));
    return BRANDS.filter((b) => brandSet.has(b));
  }, [activeCategory]);

  // Filtered products
  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (activeCategory !== "all" && p.category !== activeCategory) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        if (!p.name.toLowerCase().includes(q) && !p.nameZh.includes(q) && !p.specs.toLowerCase().includes(q) && !p.brand.toLowerCase().includes(q)) return false;
      }
      if (p.priceRange[0] > priceRange[1] || p.priceRange[1] < priceRange[0]) return false;
      if (selectedStockStatuses.length > 0 && !selectedStockStatuses.includes(p.stockStatus)) return false;
      if (selectedBrands.length > 0 && !selectedBrands.includes(p.brand)) return false;
      return true;
    });
  }, [activeCategory, searchQuery, priceRange, selectedStockStatuses, selectedBrands]);

  const activeFilterCount = (searchQuery.trim() ? 1 : 0) + (
  priceRange[0] > PRICE_MIN || priceRange[1] < PRICE_MAX ? 1 : 0) +
  selectedStockStatuses.length +
  selectedBrands.length;

  const clearAllFilters = () => {
    setSearchQuery("");
    setPriceRange([PRICE_MIN, PRICE_MAX]);
    setSelectedStockStatuses([]);
    setSelectedBrands([]);
  };

  const toggleStockStatus = (status: string) => {
    setSelectedStockStatuses((prev) =>
    prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const toggleBrand = (brand: string) => {
    setSelectedBrands((prev) =>
    prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand]
    );
  };

  // Handle product selection for comparison
  const toggleProductSelection = (product: Product) => {
    setSelectedProducts((prev) => {
      const isSelected = prev.some((p) => p.id === product.id);
      if (isSelected) {
        return prev.filter((p) => p.id !== product.id);
      } else {
        if (prev.length >= 4) return prev;
        return [...prev, product];
      }
    });
  };

  const removeFromComparison = (id: string) => {
    setSelectedProducts((prev) => prev.filter((p) => p.id !== id));
  };

  const clearComparison = () => setSelectedProducts([]);
  const openComparison = () => {if (selectedProducts.length >= 2) setShowComparison(true);};
  const closeComparison = () => setShowComparison(false);
  const isProductSelected = (id: string) => selectedProducts.some((p) => p.id === id);

  return (
    <InventoryProvider>
      {(inventoryMap, inventoryLoading) =>
      <section className="py-12 bg-white">
          <div className="max-w-8xl mx-auto px-6 md:px-10">
            {/* Category Filter Tabs */}
            <div className="flex flex-wrap gap-3 mb-8 overflow-x-auto no-scrollbar pb-2">
              {categories.map((cat) =>
            <button
              key={cat.id}
              onClick={() => {setActiveCategory(cat.id);setSelectedBrands([]);}}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold whitespace-nowrap transition-all duration-200 ${
              activeCategory === cat.id ?
              "bg-navy text-white shadow-navy-md" :
              "bg-white text-text-mid border border-border hover:border-navy/30 hover:text-navy"}`
              }>
                <Icon name={cat.icon as "Squares2X2Icon"} size={14} />
                {cat.label}
              </button>
            )}
            </div>

            {/* Search + Filter Toggle Row */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              {/* Search Bar */}
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                  <Icon name="MagnifyingGlassIcon" size={18} className="text-text-muted" />
                </div>
                <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products, brands, specs... 搜尋產品"
                className="w-full pl-11 pr-10 py-3 bg-white border border-border rounded-2xl text-sm text-navy placeholder-text-muted focus:outline-none focus:border-orange/60 focus:ring-2 focus:ring-orange/10 transition-all" />
                
                {searchQuery &&
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-3 flex items-center px-1 text-text-muted hover:text-navy">
                  
                    <Icon name="XMarkIcon" size={16} />
                  </button>
              }
              </div>

              {/* Filter Toggle Button */}
              <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-5 py-3 rounded-2xl text-sm font-bold border transition-all whitespace-nowrap ${
              showFilters ? "bg-navy text-white border-navy" : "bg-white text-navy border-border hover:border-navy/40"}`
              }>
                
                <Icon name="AdjustmentsHorizontalIcon" size={16} />
                Filters 篩選
                {activeFilterCount > 0 &&
              <span className="bg-orange text-white text-xs font-black w-5 h-5 rounded-full flex items-center justify-center">
                    {activeFilterCount}
                  </span>
              }
              </button>
            </div>

            {/* Expandable Filter Panel */}
            {showFilters &&
          <div className="bg-white border border-border rounded-3xl p-6 mb-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Price Range */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-black text-navy">Price Range 價格範圍</h4>
                    <span className="text-xs text-orange font-bold">
                      ${priceRange[0]} – ${priceRange[1] >= PRICE_MAX ? PRICE_MAX + "+" : priceRange[1]}
                    </span>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-text-muted mb-1 block">Min $</label>
                      <input
                    type="range"
                    min={PRICE_MIN}
                    max={PRICE_MAX}
                    step={50}
                    value={priceRange[0]}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      if (val <= priceRange[1]) setPriceRange([val, priceRange[1]]);
                    }}
                    className="w-full h-2 bg-border rounded-full appearance-none cursor-pointer accent-orange" />
                    
                    </div>
                    <div>
                      <label className="text-xs text-text-muted mb-1 block">Max $</label>
                      <input
                    type="range"
                    min={PRICE_MIN}
                    max={PRICE_MAX}
                    step={50}
                    value={priceRange[1]}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      if (val >= priceRange[0]) setPriceRange([priceRange[0], val]);
                    }}
                    className="w-full h-2 bg-border rounded-full appearance-none cursor-pointer accent-orange" />
                    
                    </div>
                  </div>
                </div>

                {/* Stock Status */}
                <div>
                  <h4 className="text-sm font-black text-navy mb-3">Stock Status 庫存狀態</h4>
                  <div className="space-y-2">
                    {STOCK_OPTIONS.map((opt) =>
                <button
                  key={opt.id}
                  onClick={() => toggleStockStatus(opt.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl border text-sm font-bold transition-all ${
                  selectedStockStatuses.includes(opt.id) ?
                  "bg-orange text-white border-orange" : "bg-white/90 text-navy hover:border-navy/30 hover:text-navy"}`
                  }>
                    
                        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${opt.color}`} />
                        <span>{opt.label}</span>
                        <span className="text-xs opacity-70">{opt.labelZh}</span>
                      </button>
                )}
                  </div>
                </div>

                {/* Brand Filter */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-black text-navy">Brand 品牌</h4>
                    {selectedBrands.length > 0 &&
                <button onClick={() => setSelectedBrands([])} className="text-xs text-orange hover:underline font-bold">Clear</button>
                }
                  </div>
                  <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto">
                    {availableBrands.map((brand) =>
                <button
                  key={brand}
                  onClick={() => toggleBrand(brand)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                  selectedBrands.includes(brand) ?
                  "bg-orange text-white border-orange" : "bg-white/90 text-navy hover:border-navy/30 hover:text-navy"}`
                  }>
                    
                        {brand}
                      </button>
                )}
                  </div>
                </div>
              </div>
          }

            {/* Results count + clear filters */}
            <div className="flex items-center justify-between mb-8">
              <p className="text-navy font-bold text-lg mb-1">Showing <span className="font-bold text-navy">{filtered.length}</span> products</p>
              <p className="text-text-muted text-sm mb-4">Try adjusting your search or filters</p>
              <button onClick={clearAllFilters} className="text-orange font-bold text-sm hover:underline">Clear all filters</button>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filtered.length === 0 ?
            <div className="col-span-full py-20 text-center">
                  <Icon name="MagnifyingGlassIcon" size={48} className="text-border mx-auto mb-4" />
                  <p className="text-navy font-bold text-lg mb-1">No products found</p>
                  <p className="text-text-muted text-sm mb-4">Try adjusting your search or filters</p>
                  <button onClick={clearAllFilters} className="text-orange font-bold text-sm hover:underline">Clear all filters</button>
                </div> :

            filtered.map((product) => {
              const isSelected = isProductSelected(product.id);
              const stockCfg = stockStatusConfig[product.stockStatus];
              return (
                <div
                  key={product?.id}
                  className={`group relative bg-white border rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 ${
                  isSelected ?
                  "border-orange ring-2 ring-orange/30" : "border-gray-100 hover:border-orange/30"}`
                  }>
                    {/* Image */}
                    <Link href={`/products/${product.id}`} className="block">
                    <div className="relative h-48 overflow-hidden bg-bg-light">
                      {/* Comparison Checkbox */}
                      <div className="absolute top-3 right-3 z-10">
                        <button
                          onClick={(e) => {e.preventDefault();toggleProductSelection(product);}}
                          disabled={!isSelected && selectedProducts.length >= 4}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                          isSelected ?
                          "bg-orange text-white shadow-orange-glow" :
                          "bg-white/90 text-navy hover:bg-orange hover:text-white"} disabled:opacity-50 disabled:cursor-not-allowed`
                          }
                          aria-label="Add to comparison">
                          {isSelected ?
                          <Icon name="CheckIcon" size={18} variant="solid" /> :
                          <Icon name="PlusIcon" size={18} />
                          }
                        </button>
                      </div>

                      <AppImage
                        src={product.image}
                        alt={product.alt}
                        fill
                        className="object-cover transition-transform duration-700 group-hover:scale-110"
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw" />

                      <div className="absolute top-3 left-3">
                        <span className={`text-xs font-black px-3 py-1 rounded-full ${product.badgeColor}`}>
                          {product.badge}
                        </span>
                      </div>
                      <div className="absolute inset-0 bg-navy-dark/0 group-hover:bg-navy-dark/20 transition-colors duration-300" />
                    </div>
                    </Link>

                    {/* Content */}
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-orange uppercase tracking-wide">{product.brand}</span>
                        <span className="text-xs text-text-muted">{product.moq}</span>
                      </div>
                      <Link href={`/products/${product.id}`} className="block hover:text-orange transition-colors">
                      <h3 className="font-heading font-black text-navy text-base leading-tight mb-1">
                        {product.name}
                      </h3>
                      </Link>
                      <p className="text-xs text-text-muted mb-2">{product.nameZh}</p>
                      <p className="text-xs text-text-mid leading-relaxed mb-3 font-mono">
                        {product.specs}
                      </p>

                      {/* Stock Status */}
                      <div className="flex items-center gap-1.5 mb-2">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${stockCfg.dot}`} />
                        <span className={`text-xs font-semibold ${stockCfg.color}`}>{stockCfg.label}</span>
                        <span className="text-xs text-text-muted">{stockCfg.labelZh}</span>
                      </div>

                      {/* Real-time Inventory Badge */}
                      {!inventoryLoading &&
                    <div className="mb-3">
                          <InventoryBadge productId={product?.id} inventoryMap={inventoryMap} />
                        </div>
                    }

                      {/* Price Range */}
                      <div className="flex items-center gap-1 mb-4">
                        <Icon name="TagIcon" size={12} className="text-text-muted" />
                        <span className="text-xs text-text-mid font-mono">
                          ${product.priceRange[0]} – ${product.priceRange[1]} USD
                        </span>
                      </div>

                      {/* CTA */}
                      <a
                      href="https://wa.me/85259773386"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center gap-2 bg-navy-50 hover:bg-orange text-navy hover:text-white border border-navy-100 hover:border-orange py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-200">
                        <Icon name="ChatBubbleLeftRightIcon" size={12} variant="solid" />
                        詢價 Get Quote
                      </a>
                    </div>
                  </div>);

            })
            }
            </div>

            {/* Bottom Inquiry Banner */}
            <div
            className="mt-16 rounded-4xl p-10 text-center relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, #0F2347, #1B3A6B)" }}>
              <div className="absolute inset-0 opacity-10 pointer-events-none"
            style={{ background: "radial-gradient(circle at 70% 50%, #FF8C00, transparent 60%)" }} />
              <div className="relative z-10">
                <h3 className="font-heading font-black text-white text-3xl mb-3">
                  找不到您需要的產品？
                </h3>
                <p className="text-white/65 mb-8 max-w-lg mx-auto">
                  Don't see what you need? We source custom hardware on request. Send us your BOM or product list and we'll find it.
                </p>
                <div className="flex flex-wrap gap-4 justify-center">
                  <a
                  href="https://wa.me/85259773386"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-orange hover:bg-orange-dark text-white px-8 py-4 rounded-full font-black text-sm uppercase tracking-wider transition-all shadow-orange-glow">
                    <Icon name="ChatBubbleLeftRightIcon" size={16} variant="solid" />
                    WhatsApp: +852 5977 3386
                  </a>
                  <a
                  href="/contact"
                  className="flex items-center gap-2 border border-white/30 hover:border-white/60 text-white px-8 py-4 rounded-full font-bold text-sm transition-all">
                    <Icon name="EnvelopeIcon" size={16} />
                    Send Enquiry Form
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Comparison Bar */}
          <ComparisonBar
          selectedProducts={selectedProducts}
          onRemove={removeFromComparison}
          onCompare={openComparison}
          onClear={clearComparison} />

          {/* Comparison Table Modal */}
          {showComparison &&
        <ComparisonTable products={selectedProducts} onClose={closeComparison} />
        }
        </section>
      }
    </InventoryProvider>);

}